import React, { useState, useEffect } from 'react';
import { Steps, Form, Input, Select, Button, Upload, Progress, Row, Col, Card, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import styles from './ProfileForm.module.scss';
import axios from 'axios';
import { getStoredToken } from '../utils/authUtils';
import CommonHeader from './CommonHeader';
import { useNavigate } from 'react-router-dom';
import { apiConfig } from '../config/apiConfig';
import { useVerificationStatus } from '../context/VerificationStatusContext';

const { Step } = Steps;
const { Option } = Select;
const { Dragger } = Upload;

interface FormData {
  // Step 1: Identity & Business Verification
  accountType: string;
  idType: string;
  legalName: string;
  idNumber: string;
  dateOfBirth: string;
  address: string;
  // businessName: string;
  // businessAddress: string;
  businessType: string;
  registrationNumber: string;
  // sellerName: string;
  // uploadId: any[];
  // uploadRegistrationDocument: any[];

  // Step 2: Tax Information
  taxIdNumber: string;
  taxResidencyCountry: string;
  // taxForms: any[];
}

const ProfileForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { status } = useVerificationStatus();
  const isDisabled = status === 'Approved'; // Form is read-only when approved
  const [selectedAccountType, setSelectedAccountType] = useState<string>('');
  const [selectedBusinessType, setSelectedBusinessType] = useState<string>('');
  const [customBusinessType, setCustomBusinessType] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({
    accountType: '',
    idType: '',
    legalName: '',
    idNumber: '',
    dateOfBirth: '',
    address: '',
    // businessName: '',
    // businessAddress: '',
    businessType: '',
    registrationNumber: '',
    // sellerName: '',
    // uploadId: [],
    // uploadRegistrationDocument: [],
    taxIdNumber: '',
    taxResidencyCountry: '',
    // taxForms: [],
  });

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    beforeUpload: () => false, // Prevent auto upload
    onChange(info) {
      console.log('Upload info:', info);
    },
  };

  // Get business type options based on account type
  const getBusinessTypeOptions = (accountType?: string): string[] => {
    const type = accountType || selectedAccountType;
    if (type === 'Individual') {
      return ['Sole Proprietorship', 'Professional Corporation', 'Other'];
    } else if (type === 'Business') {
      return ['LLC', 'LLP', 'C Corp', 'S Corp', 'General Partnership', 'Private Limited', 'Other'];
    }
    return [];
  };

  // Reset businessType when accountType changes
  useEffect(() => {
    form.setFieldValue('businessType', undefined);
    setSelectedBusinessType('');
    setCustomBusinessType('');
  }, [selectedAccountType, form]);

  // Fetch seller data on component mount
  useEffect(() => {
    const fetchSellerData = async () => {
      try {
        const token = getStoredToken();
        if (!token) {
          console.log('No token found');
          return;
        }

        const response = await axios.get(
          apiConfig.agentseller.me,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
            },
          }
        );

        // Prefill form with fetched data using Ant Form setFieldsValue
        if (response.data) {
          form.setFieldsValue(response.data);
          setFormData((prev) => ({ ...prev, ...response.data }));
          
          // Set account type state
          if (response.data.accountType) {
            setSelectedAccountType(response.data.accountType);
          }
          console.log('response.data.businessType',response.data.businessType)
          // Use response.data.accountType directly to avoid state closure issue
          const validOptions = getBusinessTypeOptions(response.data.accountType);
          if (response.data.businessType === 'Other' || 
              !validOptions.includes(response.data.businessType)) {
            setSelectedBusinessType('Other');
            setCustomBusinessType(response.data.businessType);
          } else {
            setSelectedBusinessType(response.data.businessType);
          }
        }
      } catch (error: any) {
        console.error('Failed to fetch seller data:', error);
        // Silently fail - user can still fill the form manually
      }
    };

    fetchSellerData();
  }, [form]);

  const steps = [
    {
      title: 'Identity & Business Verification',
      content: 'identity-verification',
    },
    {
      title: 'Tax Information',
      content: 'tax-information',
    },
    {
      title: 'Review & Submit',
      content: 'review-submit',
    },
  ];

  const handleNext = async () => {
    // If form is disabled (Approved), just move to next step
    if (isDisabled) {
      setCurrentStep(currentStep + 1);
      return;
    }

    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      const finalData = { ...formData, ...values };
      
      // Just update form data and move to next step
      setFormData({ ...finalData });
      setIsSubmitting(false);
      setCurrentStep(currentStep + 1);
    } catch (error: any) {
      console.error('Validation failed:', error);
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmitTax = async () => {
    // If form is disabled (Approved), just move to next step
    if (isDisabled) {
      setCurrentStep(currentStep + 1);
      return;
    }

    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      const finalData = { ...formData, ...values };
      
      // Update form data and move to review step
      setFormData({ ...finalData });
      setIsSubmitting(false);
      setCurrentStep(currentStep + 1);
    } catch (error: any) {
      console.error('Validation failed:', error);
      setIsSubmitting(false);
    }
  };

  const handleFinalSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      const token = getStoredToken();
      if (!token) {
        message.error('Authentication token not found. Please login again.');
        setIsSubmitting(false);
        return;
      }

      // Prepare API payload for identity verification
      const businessTypeValue = selectedBusinessType === 'Other' ? customBusinessType : formData.businessType;
      const identityPayload = {
        registrationNumber: formData.registrationNumber,
        idNumber: formData.idNumber,
        legalName: formData.legalName,
        accountType: formData.accountType,
        address: formData.address,
        idType: formData.idType,
        dateOfBirth: formData.dateOfBirth,
        businessType: businessTypeValue,
      };

      // Call identity verification API
      const identityResponse = await axios.patch(
        apiConfig.agentseller.verificationIdentity,
        identityPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
          },
        }
      );

      // Prepare API payload for tax verification
      const taxPayload = {
        taxIdNumber: formData.taxIdNumber,
        taxResidencyCountry: formData.taxResidencyCountry,
      };

      // Call tax verification API
      const taxResponse = await axios.patch(
        apiConfig.agentseller.verificationTax,
        taxPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
          },
        }
      );

      message.success('All information submitted successfully!');
      
      // Reset form and navigate to pending status screen
      setIsSubmitting(false);
      form.resetFields();
      setCurrentStep(0);
      setFormData({
        accountType: '',
        idType: '',
        legalName: '',
        idNumber: '',
        dateOfBirth: '',
        address: '',
        businessType: '',
        registrationNumber: '',
        taxIdNumber: '',
        taxResidencyCountry: '',
      });
      
      navigate('/pending');
     
    } catch (error: any) {
      console.error('Final submission failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Submission failed';
      message.error(`Error: ${errorMessage}`);
      setIsSubmitting(false);
    }
  };

  const renderIdentityVerificationStep = () => (
    <Card className={styles.formCard}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>Verify Your Identity</h2>
        {/* <Progress 
          percent={50} 
          showInfo={false} 
          strokeColor="#1890ff"
          className={styles.stepProgress}
        /> */}
      </div>
      
      <Row gutter={24}>
        <Col span={12} className={styles.formCol}>
          <Form.Item
            label="Account Type"
            name="accountType"
            rules={[{ required: true, message: 'Please select account type' }]}
          >
            <Select 
              placeholder="Select Account Type" 
              size="large" 
              disabled={isDisabled}
              onChange={(value) => {
                setSelectedAccountType(value);
              }}
            >
              <Option value="Individual">Individual</Option>
              <Option value="Business">Business</Option>
            </Select>
          </Form.Item>
        </Col>
        
        <Col span={12} className={styles.formCol}>
          <Form.Item
            label="ID Type"
            name="idType"
            rules={[{ required: true, message: 'Please select ID type' }]}
          >
            <Select placeholder="Select ID Type" size="large" disabled={isDisabled}>
              <Option value="passport">Passport</Option>
              <Option value="national_id">National ID</Option>
              <Option value="drivers_license">Driver's License</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12} className={styles.formCol}>
          <Form.Item
            label="Legal Name"
            name="legalName"
            rules={[{ required: true, message: 'Please enter legal name' }]}
          >
            <Input placeholder="Enter Legal Name" size="large" disabled={isDisabled} />
          </Form.Item>
        </Col>
        
        <Col span={12} className={styles.formCol}>
          <Form.Item
            label="ID Number"
            name="idNumber"
            rules={[{ required: true, message: 'Please enter ID number' }]}
          >
            <Input placeholder="Enter ID Number" size="large" disabled={isDisabled} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12} className={styles.formCol}>
          <Form.Item
            label="Date of Birth"
            name="dateOfBirth"
            rules={[
              { required: true, message: 'Please enter date of birth' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Check if it's a valid ISO 8601 date string (YYYY-MM-DD)
                  const iso8601Regex = /^\d{4}-\d{2}-\d{2}$/;
                  if (iso8601Regex.test(value)) {
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                      return Promise.resolve();
                    }
                  }
                  return Promise.reject(new Error('Must be a valid ISO 8601 date string (YYYY-MM-DD)'));
                },
              }
            ]}
          >
            <Input placeholder="YYYY-MM-DD" type="date" size="large" disabled={isDisabled} />
          </Form.Item>
        </Col>
        <Col span={12} className={styles.formCol}>
          <Form.Item
            label="Address"
            name="address"
            rules={[{ required: true, message: 'Please enter address' }]}
          >
            <Input placeholder="Enter Address" size="large" disabled={isDisabled} />
          </Form.Item>
        </Col>
        <Col span={12} className={styles.formCol}>
          <Form.Item
            label="Business Type"
            name="businessType"
            rules={[{ required: true, message: 'Please select business type' }]}
          >
            <Select 
              placeholder="Select Business Type" 
              size="large" 
              disabled={isDisabled}
              onChange={(value) => {
                setSelectedBusinessType(value);
                if (value !== 'Other') {
                  setCustomBusinessType('');
                }
              }}
            >
              {getBusinessTypeOptions().map((option) => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12} className={styles.formCol}>
          <Form.Item
            label="Registration Number"
            name="registrationNumber"
            rules={[{ required: true, message: 'Please enter registration number' }]}
          >
            <Input placeholder="Enter Registration Number" size="large" disabled={isDisabled} />
          </Form.Item>
        </Col>
      </Row>
      {selectedBusinessType === 'Other' && selectedAccountType && (
      <Row gutter={24}>
        <Col span={12} className={styles.formCol}>
          <Form.Item
            label="Other Business Type"
            rules={[{ required: true, message: 'Please enter other business type' }]}
          >
            <Input 
              placeholder="Enter other business type" 
              size="large" 
              disabled={isDisabled}
              value={customBusinessType}
              onChange={(e) => setCustomBusinessType(e.target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      )}
      {!isDisabled && (
      <Row gutter={24}>
        <Col span={24} className={styles.formCol}>
          <Button 
              type="primary" 
              onClick={handleNext}
              className={styles.submitBtn}
              size="large"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Next
            </Button>
        </Col>
      </Row>
      )}
      {isDisabled && (
      <Row gutter={24}>
        <Col span={24} className={styles.formCol}>
          <Button 
              type="primary" 
              onClick={handleNext}
              className={styles.submitBtn}
              size="large"
            >
              Next
            </Button>
        </Col>
      </Row>
      )}
    </Card>
  );

  const renderTaxInformationStep = () => (
    <Card className={styles.formCard}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>Tax Information</h2>
      </div>
      
      <Row gutter={24} justify="center">
        <Col span={16}>
          <Row gutter={24}>
            <Col span={24} className={styles.formCol}>
              <Form.Item
                label="Tax ID Number"
                name="taxIdNumber"
                rules={[{ required: true, message: 'Please enter tax ID number' }]}
              >
                <Input placeholder="Enter Tax ID Number" size="large" disabled={isDisabled} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24} className={styles.formCol}>
              <Form.Item
                label="Tax Residency Country"
                name="taxResidencyCountry"
                rules={[{ required: true, message: 'Please select country' }]}
              >
                <Select placeholder="Select Country" size="large" disabled={isDisabled}>                  
                  <Option value="IN">India</Option>
                  <Option value="US">United States</Option>
                  <Option value="UK">United Kingdom</Option>
                  <Option value="DE">Germany</Option>
                  <Option value="FR">France</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {!isDisabled && (
          <Row gutter={24} justify="space-between" style={{ marginTop: '30px' }}>
            <Col span={5}>
              <Button 
                onClick={handleBack}
                size="large"
                style={{ width: '100%', border: '1px solid #d9d9d9', backgroundColor: 'transparent' }}
              >
                ← Previous
              </Button>
            </Col>
            <Col span={5}>
              <Button 
                type="primary" 
                onClick={handleSubmitTax}
                className={styles.submitBtn}
                size="large"
                loading={isSubmitting}
                disabled={isSubmitting}
                style={{ width: '100%' }}
              >
                Review
              </Button>
            </Col>
          </Row>
          )}
          {isDisabled && (
          <Row gutter={24} justify="space-between" style={{ marginTop: '30px' }}>
            <Col span={5}>
              <Button 
                onClick={handleBack}
                size="large"
                style={{ width: '100%', border: '1px solid #d9d9d9', backgroundColor: 'transparent' }}
              >
                ← Previous
              </Button>
            </Col>
          </Row>
          )}
        </Col>
      </Row>
    </Card>
  );

  const renderReviewStep = () => {
    const businessTypeValue = selectedBusinessType === 'Other' ? customBusinessType : formData.businessType;
    
    return (
      <Card className={styles.formCard}>
        <div className={styles.stepHeader}>
          <h2 className={styles.stepTitle}>Review Your Information</h2>
        </div>
        
        <Row gutter={24} justify="center">
          <Col span={20}>
            {/* Identity & Business Verification Section */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>Identity & Business Verification</h3>
              <Row gutter={24}>
                <Col span={12}>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Account Type</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: '500' }}>{formData.accountType}</p>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>ID Type</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: '500' }}>{formData.idType}</p>
                  </div>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Legal Name</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: '500' }}>{formData.legalName}</p>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>ID Number</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: '500' }}>{formData.idNumber}</p>
                  </div>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Date of Birth</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: '500' }}>{formData.dateOfBirth}</p>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Address</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: '500' }}>{formData.address}</p>
                  </div>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Business Type</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: '500' }}>{businessTypeValue}</p>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Registration Number</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: '500' }}>{formData.registrationNumber}</p>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Tax Information Section */}
            <div style={{ marginBottom: '30px', paddingTop: '20px', borderTop: '1px solid #f0f0f0' }}>
              <h3 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>Tax Information</h3>
              <Row gutter={24}>
                <Col span={12}>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Tax ID Number</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: '500' }}>{formData.taxIdNumber}</p>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Tax Residency Country</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: '500' }}>{formData.taxResidencyCountry}</p>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <Row gutter={24} justify="space-between" style={{ marginTop: '30px' }}>
              <Col span={5}>
                <Button 
                  onClick={handleBack}
                  size="large"
                  style={{ width: '100%', border: '1px solid #d9d9d9', backgroundColor: 'transparent' }}
                >
                  ← Previous
                </Button>
              </Col>
              <Col span={5}>
                <Button 
                  type="primary" 
                  onClick={handleFinalSubmit}
                  className={styles.submitBtn}
                  size="large"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  style={{ width: '100%' }}
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderIdentityVerificationStep();
      case 1:
        return renderTaxInformationStep();
      case 2:
        return renderReviewStep();
      default:
        return null;
    }
  };

  return (
    <div className="signup-container">
      <CommonHeader />
      
      {/* <main className="signup-main"> */}
      <main className="signup-main profilePage">
        <div className={styles.multiStepForm}>
          {/* <div className={styles.stepsContainer}>
            <Steps current={currentStep} className={styles.customSteps}>
              {steps.map((step, index) => (
                <Step key={index} title={step.title} />
              ))}
            </Steps>
          </div> */}

          <Form form={form} layout="vertical" className={styles.stepForm}>
            {renderStepContent()}
          </Form>

          {/* <div className={styles.formActions}>
            {currentStep > 0 && (
              <Button 
                onClick={handleBack} 
                className={styles.backBtn}
                size="large"
              >
                Back
              </Button>
            )}
            
            <Button 
              type="primary" 
              onClick={handleSubmit}
              className={styles.submitBtn}
              size="large"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Submit
            </Button>
          </div> */}
        </div>
      </main>
    </div>
  );
};

export default ProfileForm;